import React, { useEffect, useState } from "react";
import { Send, Plus, Mail, Sparkles, Loader2, X, Check, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const tabs = ["Chat", "Campaign Ideas", "My Campaigns", "Contacts", "Analytics"];
const tones = ["Engaging", "Warm", "Urgent", "Professional", "Playful"];

const EmailMarketing = () => {
  const [tab, setTab] = useState("Chat");
  const [data, setData] = useState({ campaigns: [], contacts_count: 0, ideas: [], analytics: {} });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // Composer state
  const [c, setC] = useState({ name: "", topic: "", tone: "Engaging", subject: "", body: "" });
  const [subjects, setSubjects] = useState([]);
  const [genSubj, setGenSubj] = useState(false);
  const [genBody, setGenBody] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  // Bulk send modal
  const [sendCamp, setSendCamp] = useState(null);
  const [sendEmails, setSendEmails] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = () => api.get("/email/dashboard").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput(""); setSending(true);
    try {
      const { data } = await api.post("/email/chat", { message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } finally { setSending(false); }
  };

  const generateSubjects = async () => {
    if (!c.topic.trim()) return toast.error("Add a topic first");
    setGenSubj(true);
    try {
      const { data } = await api.post("/email/generate-subjects", { topic: c.topic, tone: c.tone.toLowerCase() });
      setSubjects(data.subjects || []);
    } catch { toast.error("Generate failed"); }
    finally { setGenSubj(false); }
  };

  const generateBody = async () => {
    if (!c.topic.trim()) return toast.error("Add a topic first");
    setGenBody(true);
    try {
      const { data } = await api.post("/email/generate-body", { topic: c.topic, tone: c.tone.toLowerCase() });
      setC((cur) => ({ ...cur, body: data.body }));
    } catch { toast.error("Generate failed"); }
    finally { setGenBody(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim() || !c.subject || !c.body) return toast.error("Need subject, body, and test email");
    setSendingTest(true);
    try {
      await api.post("/email/send-test", { subject: c.subject, body: c.body, to_email: testEmail });
      toast.success(`Test sent to ${testEmail} (MOCKED)`);
    } catch { toast.error("Send failed"); }
    finally { setSendingTest(false); }
  };

  const create = async () => {
    if (!c.name || !c.subject || !c.body) return toast.error("Fill name, subject, and body");
    await api.post("/email/campaign", { name: c.name, subject: c.subject, body: c.body, type: "email" });
    toast.success("Campaign created");
    closeCreate();
    load();
    setTab("My Campaigns");
  };

  const closeCreate = () => {
    setShowCreate(false);
    setC({ name: "", topic: "", tone: "Engaging", subject: "", body: "" });
    setSubjects([]); setTestEmail("");
  };

  const sendBulk = async () => {
    if (!sendCamp) return;
    const emails = sendEmails.split(/[\n,;]/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) return toast.error("Add at least one email");
    if (!confirm(`Send REAL email to ${emails.length} address${emails.length > 1 ? "es" : ""} via Brevo? Free tier: 300/day.`)) return;
    setBulkSending(true); setBulkResult(null);
    try {
      const { data } = await api.post("/email/send-campaign", { campaign_id: sendCamp.id, to_emails: emails, content_type: "plain" });
      setBulkResult(data);
      toast.success(`Sent ${data.sent}/${data.sent + data.failed}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBulkSending(false); }
  };

  const closeSend = () => { setSendCamp(null); setSendEmails(""); setBulkResult(null); };

  return (
    <PageContainer>
      <PageHeader title="Email Marketing" subtitle="AI-written campaigns, end-to-end." testId="email-header"
        actions={
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${data.live ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`} data-testid="email-live-badge">
              {data.live ? `● ${data.provider || "Brevo"} Live` : "○ Mocked"}
            </span>
            <button onClick={() => setShowCreate(true)} className="op-btn-primary flex items-center gap-1.5" data-testid="email-create-campaign-top"><Plus size={16} /> New Campaign</button>
          </div>
        } />
      <div className="flex gap-2 border-b border-gray-200 mb-6 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${tab === t ? "border-[#7C3AED] text-[#7C3AED]" : "border-transparent text-gray-600"}`} data-testid={`email-tab-${t.replace(/\s/g,'-').toLowerCase()}`}>{t}</button>
        ))}
      </div>

      {tab === "Chat" && (
        <div className="max-w-3xl mx-auto">
          <div className="op-card p-4 min-h-[300px] mb-4 space-y-3" data-testid="email-chat">
            {messages.length === 0 && <p className="text-center text-gray-500 py-12">Ask me to write a campaign — "Write a Mother's Day promo for my bakery"</p>}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.role === "user" ? "bg-[#7C3AED] text-white" : "bg-gray-100 text-gray-900"}`}>
                  <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-3 flex-wrap">
            {["Write a welcome email", "Create a weekend promo", "Re-engage cold leads"].map((p) => (
              <button key={p} onClick={() => { setInput(p); setTimeout(send, 50); }} className="op-pill-inactive">{p}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask the email AI..." className="op-input flex-1" data-testid="email-chat-input" />
            <button onClick={send} disabled={sending} className="op-btn-primary" data-testid="email-chat-send">{sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
          </div>
        </div>
      )}

      {tab === "Campaign Ideas" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.ideas.map((i) => (
            <div key={i.id} className="op-card p-5">
              <p className="font-semibold text-gray-900 mb-1">{i.title}</p>
              <p className="text-sm text-gray-500 mb-4">{i.preview}</p>
              <button onClick={() => { setC((cur) => ({ ...cur, name: i.title, topic: i.title })); setShowCreate(true); }} className="op-btn-primary text-sm py-2">Use idea →</button>
            </div>
          ))}
        </div>
      )}

      {tab === "My Campaigns" && (
        <div>
          {data.campaigns.length === 0 ? (
            <div className="op-card"><EmptyState icon={Mail} title="No campaigns yet" description="Create your first email campaign." action={<button onClick={() => setShowCreate(true)} className="op-btn-primary">Create →</button>} /></div>
          ) : (
            <div className="op-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr className="text-left text-xs font-semibold text-gray-600 uppercase"><th className="px-5 py-3">Name</th><th className="px-5 py-3">Subject</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Created</th><th className="px-5 py-3"></th></tr></thead>
                <tbody>{data.campaigns.map((cp) => (
                  <tr key={cp.id} className="border-t border-gray-100">
                    <td className="px-5 py-3 font-medium">{cp.name}</td>
                    <td className="px-5 py-3 text-gray-600 truncate max-w-xs">{cp.subject}</td>
                    <td className="px-5 py-3 capitalize"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cp.status === "sent" ? "bg-emerald-100 text-emerald-700" : cp.status === "failed" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"}`}>{cp.status}</span></td>
                    <td className="px-5 py-3 text-gray-500 text-xs">{new Date(cp.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3">
                      {cp.status === "draft" && (
                        <button onClick={() => setSendCamp(cp)} className="op-btn-primary text-xs py-1.5 px-3 flex items-center gap-1" data-testid={`email-send-${cp.id}`}>
                          <Zap size={12} /> Send
                        </button>
                      )}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "Contacts" && (
        <div className="op-card p-8 text-center">
          <p className="text-3xl font-bold text-gray-900">{data.contacts_count}</p>
          <p className="text-gray-500 mt-2">Total contacts. Import a CSV to sync.</p>
          <button className="op-btn-primary mt-5 mx-auto">Import Contacts →</button>
        </div>
      )}

      {tab === "Analytics" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="op-card p-5"><p className="text-xs text-gray-500 uppercase">Open Rate</p><p className="text-3xl font-bold mt-2">{data.analytics.open_rate}%</p></div>
          <div className="op-card p-5"><p className="text-xs text-gray-500 uppercase">Click Rate</p><p className="text-3xl font-bold mt-2">{data.analytics.click_rate}%</p></div>
          <div className="op-card p-5"><p className="text-xs text-gray-500 uppercase">Sent</p><p className="text-3xl font-bold mt-2">{data.analytics.sent?.toLocaleString()}</p></div>
        </div>
      )}

      {/* AI Composer Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeCreate} data-testid="email-composer">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">New Email Campaign</h2>
              <button onClick={closeCreate}><X size={18} className="text-gray-400" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-700">Campaign name</label>
                <input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} className="op-input mt-1" placeholder="Mother's Day promo" data-testid="email-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Topic / brief</label>
                <input value={c.topic} onChange={(e) => setC({ ...c, topic: e.target.value })} className="op-input mt-1" placeholder="What is this email about?" data-testid="email-topic" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Tone</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {tones.map((t) => (
                    <button key={t} onClick={() => setC({ ...c, tone: t })} className={c.tone === t ? "op-pill-active" : "op-pill-inactive"}>{t}</button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-700">Subject line</label>
                  <button onClick={generateSubjects} disabled={genSubj || !c.topic} className="text-xs text-[#7C3AED] font-semibold flex items-center gap-1" data-testid="email-gen-subjects">
                    {genSubj ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Generate 5 options
                  </button>
                </div>
                <input value={c.subject} onChange={(e) => setC({ ...c, subject: e.target.value })} className="op-input" placeholder="Pick from below or type your own" data-testid="email-subject" />
                {subjects.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {subjects.map((s, i) => (
                      <button key={i} onClick={() => setC({ ...c, subject: s })} className="w-full text-left text-sm px-3 py-2 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-[#7C3AED] flex items-center justify-between" data-testid={`email-subj-${i}`}>
                        <span>{s}</span>
                        {c.subject === s && <Check size={14} className="text-[#7C3AED]" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-700">Body</label>
                  <button onClick={generateBody} disabled={genBody || !c.topic} className="text-xs text-[#7C3AED] font-semibold flex items-center gap-1" data-testid="email-gen-body">
                    {genBody ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Write with AI
                  </button>
                </div>
                <textarea rows={8} value={c.body} onChange={(e) => setC({ ...c, body: e.target.value })} className="op-input font-mono text-sm" placeholder="Email body…" data-testid="email-body" />
              </div>

              <div className="border-t border-gray-100 pt-4">
                <label className="text-xs font-semibold text-gray-700">Send test email</label>
                <div className="flex gap-2 mt-1">
                  <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} type="email" className="op-input flex-1" placeholder="you@example.com" data-testid="email-test-to" />
                  <button onClick={sendTest} disabled={sendingTest || !testEmail || !c.subject || !c.body} className="op-btn-secondary flex items-center gap-1" data-testid="email-send-test">
                    {sendingTest ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
              <button onClick={closeCreate} className="op-btn-secondary">Cancel</button>
              <button onClick={create} disabled={!c.name || !c.subject || !c.body} className="op-btn-primary" data-testid="email-create-submit">Create campaign →</button>
            </div>
          </div>
        </div>
      )}

      {sendCamp && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeSend} data-testid="email-send-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2"><Zap size={18} className="text-[#7C3AED]" /> Send "{sendCamp.name}"</h2>
              <button onClick={closeSend}><X size={18} className="text-gray-400" /></button>
            </div>
            {!bulkResult ? (
              <>
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Subject</p>
                  <p className="text-sm font-semibold mb-3">{sendCamp.subject}</p>
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Body</p>
                  <p className="text-xs text-gray-700 whitespace-pre-line line-clamp-4">{sendCamp.body}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-800">
                  ⚠️ Real Brevo email — counts against your free 300/day quota.
                </div>
                <label className="text-xs font-semibold text-gray-700">Recipient emails (one per line, comma, or semicolon)</label>
                <textarea value={sendEmails} onChange={(e) => setSendEmails(e.target.value)} rows={5} className="op-input font-mono text-xs mt-1" placeholder="alice@example.com&#10;bob@example.com" data-testid="email-bulk-list" />
                <div className="flex justify-end gap-2 mt-5">
                  <button onClick={closeSend} className="op-btn-secondary" data-testid="email-bulk-cancel">Cancel</button>
                  <button onClick={sendBulk} disabled={bulkSending || !sendEmails.trim()} className="op-btn-primary flex items-center gap-2" data-testid="email-bulk-send">
                    {bulkSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {bulkSending ? "Sending..." : "Send now"}
                  </button>
                </div>
              </>
            ) : (
              <div data-testid="email-bulk-result">
                <div className="text-center py-3 mb-4">
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-2"><Check size={26} className="text-emerald-600" /></div>
                  <p className="text-2xl font-bold">{bulkResult.sent}/{bulkResult.sent + bulkResult.failed}</p>
                  <p className="text-sm text-gray-500">emails sent successfully</p>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-xs font-semibold text-red-700 mb-2">Failures:</p>
                    <ul className="text-xs text-red-600 space-y-1">
                      {bulkResult.errors.map((e, i) => <li key={i}>{e.to}: {e.error}</li>)}
                    </ul>
                  </div>
                )}
                <button onClick={closeSend} className="op-btn-primary w-full">Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};
export default EmailMarketing;
